import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { useToasts } from 'common/toast';
import styled from 'styled-components';
import FocusPlanAddModal from './FocusPlanAddModal';
import { sentimentForSignal } from '@/areas/culture-hub/components/interventions/action-planner/sentiment';
import { getPillStyleForRelevanceLabel } from './pillStyles';
import {
  useEmergentThemeTeamDetailQuery,
  SuggestedIntervention,
  ThemeActionPlan,
} from '@/areas/culture-hub/components/interventions/action-planner/api';
import { Recommendation } from '@/areas/culture-hub/types/api';
import { useGetAssessmentPeriodsByScopeQuery } from '@/areas/assessment-schedule/api/assessmentPeriodApi';
import { useActionPlanOperations } from '@/areas/culture-hub/hooks/useActionPlanOperations';
import { getSelectedActionPlanTaskKeys, addRecommendationToPlan } from '@/areas/culture-hub/slices/cultureHubInterventionsSlice';
import { ACTION_PLAN_TYPE_INTERVENTIONS } from '@/areas/culture-hub/constants';
import { useCultureHubQueryParams } from '@/areas/culture-hub/hooks/useCultureHubQueryParams';
import { Icon } from 'semantic-ui-react';
import ThemeTitleIcon from '@/common/components/ThemeTitleIcon';
import {
  getCurrentOrganizationId,
  getLeadershipTeams,
  getSelectedAssessmentPeriod,
  getMemberId,
  isCurrentMemberTeamLeader,
} from '@/common/selectors/auth';
import userContextSlice from '@/common/slices/userContextSlice';
import { useCultureHubTopBarControls } from '@/areas/culture-hub/components/shared/useCultureHubTopBarControls';
import {
  BodyText,
  H2,
  PageContent,
  PageHeader,
  PageSection,
  StyledCard,
} from '@/common/styles/page';

interface RawEmergentTheme {
  theme_uid?: string;
  theme_title?: string;
  theme_description?: string;
  feedback_count?: number;
  signal_strength?: number;
  strength?: number;
  pillar_tags?: string[];
}

interface ThemeWithDetails extends RawEmergentTheme {
  team_id?: number;
  team_name?: string;
  action_plans?: ThemeActionPlan[];
  suggested_interventions?: SuggestedIntervention[];
}

function convertSuggestedInterventionToRecommendation(suggestion: SuggestedIntervention): Recommendation {
  return {
    recommendation_template_id: suggestion.recommendation_template_id,
    recommendation_name: suggestion.recommendation_name,
    task_index: suggestion.task_index,
    task: suggestion.task,
    description: suggestion.description ?? undefined,
    time: suggestion.time ?? undefined,
    time_estimate: suggestion.time_estimate ?? undefined,
    practical_example: suggestion.practical_example ?? undefined,
    softSkills: suggestion.softSkills ?? undefined,
    references: suggestion.references ?? undefined,
    supportingScience: suggestion.supportingScience ?? undefined,
    why_question: suggestion.why_question ?? undefined,
    relevance_label: suggestion.relevance_label,
  };
}

const getRelevancePillVariant = (label?: string) => {
  if (label === 'Highly Recommended') return 'high' as const;
  if (label === 'Recommended') return 'medium' as const;
  if (label === 'Worth Considering') return 'low' as const;
  if (label === 'Less Relevant') return 'minimal' as const;
  return 'minimal' as const;
};

const ThemeDetail: React.FC<{
  theme: ThemeWithDetails;
  isExpanded: boolean;
  onSuggestionSelect: (suggestion: SuggestedIntervention) => void;
  onToggleExpand: () => void;
}> = ({
  theme,
  isExpanded,
  onSuggestionSelect,
  onToggleExpand,
}) => {
    const suggestions = theme.suggested_interventions ?? [];
    const gridRef = useRef<HTMLDivElement | null>(null);
    const [cardsPerRow, setCardsPerRow] = useState(1);

    useEffect(() => {
      if (!gridRef.current) return;

      const updateCardsPerRow = () => {
        const width = gridRef.current?.clientWidth ?? 0;
        const gap = 16;
        const cardMinWidth = 280;
        const count = Math.max(1, Math.floor((width + gap) / (cardMinWidth + gap)));
        setCardsPerRow(count);
      };

      updateCardsPerRow();
      const observer = new ResizeObserver(() => updateCardsPerRow());
      observer.observe(gridRef.current);

      return () => observer.disconnect();
    }, [suggestions.length]);

    if (suggestions.length === 0) {
      return (
        <p style={{ color: 'var(--color-muted)', fontSize: 13 }}>
          No suggested interventions are available for this theme yet.
        </p>
      );
    }

    const visibleSuggestions = isExpanded ? suggestions : suggestions.slice(0, cardsPerRow);
    const hasMoreCards = suggestions.length > cardsPerRow;

    return (
      <SuggestionSection>
        <SuggestionGrid ref={gridRef}>
          {visibleSuggestions.map(suggestion => (
            <SuggestionCard
              key={`${suggestion.recommendation_template_id}-${suggestion.task_index}`}
              role="button"
              tabIndex={0}
              onClick={() => onSuggestionSelect(suggestion)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  onSuggestionSelect(suggestion);
                }
              }}
            >
              <SuggestionPill style={{
                background: getPillStyleForRelevanceLabel(suggestion.relevance_label).background,
                color: getPillStyleForRelevanceLabel(suggestion.relevance_label).color,
                borderColor: getPillStyleForRelevanceLabel(suggestion.relevance_label).borderColor,
              }}>
                {suggestion.relevance_label}
              </SuggestionPill>
              <SuggestionCardHeader>
                <SuggestionCardTitle title={suggestion.task || suggestion.recommendation_name}>
                  {suggestion.task || suggestion.recommendation_name}
                </SuggestionCardTitle>
              </SuggestionCardHeader>
              {suggestion.description && (
                <BodyText color="var(--color-muted)">
                  {suggestion.description}
                </BodyText>
              )}
            </SuggestionCard>
          ))}
        </SuggestionGrid>

        {hasMoreCards && (
          <ExpandToggleRow>
            <ExpandToggleButton
              type="button"
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Collapse suggestions' : 'Expand suggestions'}
              onClick={onToggleExpand}
            >
              <Icon name={isExpanded ? 'caret up' : 'caret down'} size="large" />
              <ExpandToggleButtonText>{isExpanded ? 'Show Less' : 'Show More'}</ExpandToggleButtonText>
            </ExpandToggleButton>
          </ExpandToggleRow>
        )}
      </SuggestionSection>
    );
  };

const FocusPlanRecommendations: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const organizationId = useSelector(getCurrentOrganizationId);
  const employeeId = useSelector(getMemberId);
  const selectedActionPlanTaskKeys = useSelector(getSelectedActionPlanTaskKeys);
  const isTeamLeader = useSelector(isCurrentMemberTeamLeader);
  const [selectedSuggestion, setSelectedSuggestion] = useState<SuggestedIntervention | null>(null);

  const { assessmentPeriodId: queryAssessmentPeriodId, organizationId: queryOrganizationId, teamId: queryTeamId, isMostRecentClosedAssessmentPeriod } = useCultureHubQueryParams();
  const selectedAssessmentPeriod = useSelector(getSelectedAssessmentPeriod);
  const leadershipTeams = useSelector(getLeadershipTeams);
  const selectedTeamIdGlobal = useSelector(userContextSlice.selectors.getSelectedTeamId as any) as number | undefined;

  const { data: assessmentPeriods, isLoading: isAssessmentPeriodsLoading, isFetching: isAssessmentPeriodsFetching } = useGetAssessmentPeriodsByScopeQuery(undefined, { skip: !organizationId });

  const filteredAssessmentPeriods = React.useMemo(() => {
    if (!selectedTeamIdGlobal || !assessmentPeriods?.length) {
      return assessmentPeriods;
    }
    return assessmentPeriods.filter(period =>
      leadershipTeams?.some(team =>
        Array.isArray(team.assessments) && team.assessments.includes(period.id)
      )
    );
  }, [assessmentPeriods, leadershipTeams, selectedTeamIdGlobal]);

  const topBarExtraControls = null;

  const handleTopBarTeamChange = useCallback((id: number | null) => {
    const teamId = id !== null ? Number(id) : null;
    dispatch(userContextSlice.actions.setSelectedTeamId(teamId));
  }, [dispatch]);

  const handleTopBarAssessmentPeriodChange = useCallback((value: string | number | undefined, period?: any) => {
    const id = typeof value === 'string' ? parseInt(value, 10) : value;
    const selected = period ?? assessmentPeriods?.find(p => p.id === id);
    if (selected) {
      dispatch(
        userContextSlice.actions.setSelectedAssessmentPeriod({
          selectedAssessmentPeriod: selected,
          assessmentPeriods: assessmentPeriods || [],
        })
      );
    }
  }, [dispatch, assessmentPeriods]);

  useCultureHubTopBarControls({
    showSelector: true,
    selectedTeamIdGlobal,
    leadershipTeams: leadershipTeams || [],
    selectedAssessmentPeriodId: selectedAssessmentPeriod?.id ?? null,
    filteredAssessmentPeriods,
    isLoadingPeriods: isAssessmentPeriodsLoading || isAssessmentPeriodsFetching,
    onTeamChange: handleTopBarTeamChange,
    onAssessmentPeriodChange: handleTopBarAssessmentPeriodChange,
    extraControls: topBarExtraControls,
  });

  const { saveAllTasks, determineLeaderAndCategory, isSaving } = useActionPlanOperations({
    selectedAssessmentPeriodId: queryAssessmentPeriodId,
    organizationId: queryOrganizationId,
    currentUserId: employeeId ?? null,
    currentUserDebriefSchedule: null,
    categoryDelegates: [],
    existingActionPlans: undefined,
    actionPlanType: ACTION_PLAN_TYPE_INTERVENTIONS,
    teamId: queryTeamId,
  });

  const {
    data: teamThemeData,
    isLoading,
    isFetching,
    isError,
  } = useEmergentThemeTeamDetailQuery(
    {
      assessmentPeriodId: queryAssessmentPeriodId as number,
      teamId: queryTeamId as number,
    },
    {
      skip: !queryAssessmentPeriodId || !queryOrganizationId || !queryTeamId,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
    }
  );

  const themes = queryTeamId ? teamThemeData?.themes ?? [] : [];
  const [expandedThemes, setExpandedThemes] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setSelectedSuggestion(null);
  }, [queryTeamId, queryAssessmentPeriodId]);

  const isSuggestionAlreadyAdded = selectedSuggestion
    ? selectedActionPlanTaskKeys.some(
      (actionPlan) =>
        actionPlan.recommendation_template_id === selectedSuggestion.recommendation_template_id &&
        actionPlan.task_index === selectedSuggestion.task_index
    )
    : false;

  const handleAddToFocusPlan = async () => {
    if (!selectedSuggestion) return;

    if (!isMostRecentClosedAssessmentPeriod) {
      addToast('You can only add focus plan items in the latest closed assessment period.', {
        appearance: 'error',
        autoDismiss: true,
      });
      return;
    }

    const { leaderId, categoryUid } = determineLeaderAndCategory();
    const recommendation = convertSuggestedInterventionToRecommendation(selectedSuggestion);

    dispatch(addRecommendationToPlan(recommendation));

    const result = await saveAllTasks([recommendation], leaderId, categoryUid);
    if (!result.success) {
      addToast(result.error || 'Failed to add focus area', {
        appearance: 'error',
        autoDismiss: true,
      });
      return;
    }

    addToast('Focus area added to your plan', {
      appearance: 'success',
      autoDismiss: true,
    });
    setSelectedSuggestion(null);
  };

  const loading = isLoading || isFetching;

  return (
    <PageContent>
      <PageSection >
        <PageSection>
          <PageHeader
            title="Build Your Focus Plan"
            caption="The following themes are prominent in your team's feedback. Select from the recommended activities to strengthen your leadership and improve your team's culture."
            backText="Return to Focus Plan"
            onBack={() => navigate('/app/culture-hub/interventions')}
          />
        </PageSection>

        <PageSection padtop="16px">
          {loading && <LoadingSpinner />}

          {!loading && isError && (
            <EmptyStateCard>
              <EmptyStateText>Unable to load focus plan recommendations.</EmptyStateText>
            </EmptyStateCard>
          )}

          {!loading && !isError && themes.length === 0 && (
            <EmptyStateCard>
              <EmptyStateText>No themes available for the selected assessment period yet.</EmptyStateText>
            </EmptyStateCard>
          )}

          {!loading && !isError && (
            <ThemeList>
              {themes.map(theme => {
                const uid = theme.theme_uid as string;
                const sentiment = sentimentForSignal(theme.signal_strength);
                const count = theme.feedback_count ?? 0;

                return (
                  <ThemeSection key={uid}>
                    <ThemeHeaderRow>
                        <ThemeTitleIcon variant={sentiment.variant}>
                          <Icon
                            name={
                              sentiment.variant === 'positive' || sentiment.variant === 'very-positive'
                                ? 'thumbs up'
                                : sentiment.variant === 'neutral'
                                  ? 'comment alternate'
                                  : 'warning sign'
                            }
                          />
                        </ThemeTitleIcon>
                        <ThemeTitle>{theme.theme_title ?? 'Untitled theme'}</ThemeTitle>
                    </ThemeHeaderRow>
                    {theme.theme_description && (
                      <ThemeDescriptionRow>
                        <ThemeDescription color="var(--color-muted)">
                          {theme.theme_description}
                          {count > 0 && (count === 1 ? ` The feedback from 1 response was ${sentiment.variant === 'very-negative'
                            ? 'very negative'
                            : sentiment.variant === 'negative'
                              ? 'negative'
                              : sentiment.variant === 'neutral'
                                ? 'mixed'
                                : sentiment.variant === 'positive'
                                  ? 'positive'
                                  : 'very positive'
                            }. ` : ` Most of the feedback from ${count} responses was ${sentiment.variant === 'very-negative'
                              ? 'very negative'
                              : sentiment.variant === 'negative'
                                ? 'negative'
                                : sentiment.variant === 'neutral'
                                  ? 'mixed'
                                  : sentiment.variant === 'positive'
                                    ? 'positive'
                                    : 'very positive'
                            }. `)}
                        </ThemeDescription>
                      </ThemeDescriptionRow>
                    )}

                    <ThemeDetail
                      theme={theme}
                      isExpanded={Boolean(expandedThemes[uid])}
                      onSuggestionSelect={setSelectedSuggestion}
                      onToggleExpand={() => setExpandedThemes(prev => ({
                        ...prev,
                        [uid]: !prev[uid],
                      }))}
                    />
                  </ThemeSection>
                );
              })}
            </ThemeList>
          )}
          {selectedSuggestion && (
            <FocusPlanAddModal
              open={Boolean(selectedSuggestion)}
              suggestion={selectedSuggestion}
              onClose={() => setSelectedSuggestion(null)}
              onAdd={handleAddToFocusPlan}
              isSaving={isSaving}
              isSuggestionAlreadyAdded={isSuggestionAlreadyAdded}
            />
          )}
        </PageSection>
      </PageSection>
    </PageContent>
  );
};

const ThemeList = styled.div`
  display: grid;
  gap: 28px;
`;

const ThemeSection = styled.div`
  display: grid;
`;

const ThemeHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding-top: 16px;
  padding-bottom: 8px;
`;

const ThemeDescriptionRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  padding-bottom: 28px;
`;

const ThemeTitle = styled(H2)`
  margin: 0;
  line-height: 1.1;
  font-size: 20px;
  font-weight: 525;
`;

const ThemeDescription = styled(BodyText)`
  margin-left: 42px;
  font-size: 13px;
  line-height: 1.35;
`;

const SuggestionSection = styled.div`
  display: grid;
  padding-top: 12px;
  padding-left: 24px;
  padding-right: 24px;
`;

const SuggestionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
`;

const ExpandToggleRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-right: 14px;
`;

const ExpandToggleButtonText = styled.span`
  font-size: 0.85rem;
  font-weight: 500;
  color: #757b86;
    &:hover {
    background: transparent;
    color: #2f79fa;
  }

`;

const ExpandToggleButton = styled.button`
  height: 24px;
  border: none;
  background: transparent;
  color: #757b86;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 0;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: transparent;
    color: #2f79fa;
  }
`;

const SuggestionCard = styled(StyledCard)`
  padding: 24px 18px 18px;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
  overflow: visible;
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
  }
`;

const SuggestionCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
`;

const SuggestionCardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  min-width: 0;
  flex: 1;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const SuggestionPill = styled.div`
  position: absolute;
  top: -12px;
  right: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  white-space: nowrap;
  text-overflow: ellipsis;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  border: 1px solid;
`;

const EmptyStateCard = styled(StyledCard)`
  padding: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 160px;
`;

const EmptyStateText = styled.div`
  color: #666;
  font-size: 14px;
`;

export default FocusPlanRecommendations;
